import { useEffect } from 'react'
import { loadSettings } from './settings'

const STORAGE_KEY = 'gps-journal-entries'
const HABIT_STATE_KEY = 'gps-journal-daily-habits'
const PROJECT_TIME_KEY = 'gps-journal-project-time'

export interface JournalEntry {
  date: string // YYYY-MM-DD
  hpCompleted?: number
  hpTotal?: number
  ppCompleted?: number // minutes
  ppTotal?: number // minutes (goal at time of save)
  wins?: [string, string, string]
  blocker?: string
  tomorrowsTweak?: string
  savedAt?: string // ISO timestamp of last update
}

function todayKey(): string {
  const d = new Date()
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
}

export function loadEntries(): Record<string, JournalEntry> {
  if (typeof window === 'undefined') return {}
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY)
    if (!raw) return {}
    return JSON.parse(raw) as Record<string, JournalEntry>
  } catch {
    return {}
  }
}

export function loadEntry(date: string): JournalEntry | undefined {
  return loadEntries()[date]
}

export function saveEntry(date: string, partial: Partial<JournalEntry>) {
  if (typeof window === 'undefined') return
  try {
    const all = loadEntries()
    all[date] = {
      ...(all[date] ?? { date }),
      ...partial,
      date,
      savedAt: new Date().toISOString(),
    }
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(all))
  } catch {
    // ignore
  }
}

/**
 * Read the raw daily habit/project-time records. If they are for a date that
 * is not today (i.e. a day has passed since the user last opened the app),
 * persist that data as a journal entry for that date so it isn't lost when
 * the per-day stores overwrite themselves with today's values.
 */
export function flushPreviousDay() {
  if (typeof window === 'undefined') return
  const today = todayKey()

  // Habits
  try {
    const raw = window.localStorage.getItem(HABIT_STATE_KEY)
    if (raw) {
      const record = JSON.parse(raw) as {
        date: string
        habits: { text: string; completed: boolean }[]
      }
      if (record.date && record.date !== today) {
        const completed = record.habits.filter((h) => h.completed).length
        saveEntry(record.date, {
          hpCompleted: completed,
          hpTotal: record.habits.length,
        })
      }
    }
  } catch {
    // ignore
  }

  // Project time
  try {
    const raw = window.localStorage.getItem(PROJECT_TIME_KEY)
    if (raw) {
      const record = JSON.parse(raw) as {
        date: string
        minutesLogged: number
        workedOn?: string
      }
      if (record.date && record.date !== today) {
        const settings = loadSettings()
        const goalMinutes = settings.ppHours * 60 + settings.ppMinutes
        saveEntry(record.date, {
          ppCompleted: record.minutesLogged,
          ppTotal: goalMinutes,
        })
      }
    }
  } catch {
    // ignore
  }
}

/**
 * Hook that:
 * 1. Flushes any stale (yesterday's) data into journal entries on mount.
 * 2. Polls every minute to detect midnight rollover while the app is open,
 *    and flushes the just-finished day before the per-day stores reset.
 */
export function useJournalAutoSave() {
  useEffect(() => {
    flushPreviousDay()
    let lastSeen = todayKey()
    const interval = window.setInterval(() => {
      const now = todayKey()
      if (now !== lastSeen) {
        flushPreviousDay()
        lastSeen = now
      }
    }, 60_000)
    return () => window.clearInterval(interval)
  }, [])
}
